import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, Package, ArrowRight, XCircle, ImageIcon } from "lucide-react";
import { statusOf } from "@/lib/orderStatus";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function ResiDialog({ order, onClose }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!order) { return undefined; }
    let obj = "";
    setUrl(""); setErr(false);
    api.get(`/orders/${order.id}/resi-image`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); })
      .catch(() => setErr(true));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [order]);
  return (
    <Dialog open={!!order} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="resi-dialog">
        <DialogHeader>
          <DialogTitle>Bukti Resi {order ? `#${order.order_number}` : ""}</DialogTitle>
          <DialogDescription>{order?.tracking_number ? `No. Resi: ${order.tracking_number}` : "Foto bukti pengiriman dari Rooftop Print"}</DialogDescription>
        </DialogHeader>
        <div className="min-h-[240px] flex items-center justify-center bg-slate-50 rounded-xl overflow-hidden">
          {err ? <p className="text-sm text-slate-500">Gagal memuat bukti resi.</p>
            : url ? <img src={url} alt="Bukti resi" className="max-h-[70vh] w-auto object-contain" data-testid="resi-dialog-image" />
            : <div className="w-full h-[240px] bg-slate-200 animate-pulse" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const REASONS = [
  { code: "ubah_produk", label: "Ingin mengubah rincian produk (jumlah/varian)" },
  { code: "ubah_alamat", label: "Ingin mengubah alamat pengiriman" },
  { code: "ubah_pembayaran", label: "Ingin mengganti metode pembayaran" },
  { code: "lainnya", label: "Lainnya" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [reason, setReason] = useState("ubah_produk");
  const [reasonText, setReasonText] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [resiOrder, setResiOrder] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  const openCancel = (o) => { setCancelOrder(o); setReason("ubah_produk"); setReasonText(""); };

  const confirmCancel = async () => {
    if (reason === "lainnya" && !reasonText.trim()) { toast.error("Mohon isi alasan pembatalan"); return; }
    setCancelling(true);
    try {
      const res = await api.post(`/orders/${cancelOrder.id}/cancel`, { reason_code: reason, reason_text: reasonText.trim() });
      setOrders((prev) => prev.map((o) => (o.id === cancelOrder.id ? res.data : o)));
      toast.success("Pesanan berhasil dibatalkan");
      setCancelOrder(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membatalkan pesanan");
    } finally { setCancelling(false); }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#00133F] mb-8">Pesanan Saya</h1>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#F0005A]" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-24 rounded-2xl border border-dashed border-slate-300">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5"><Package className="w-7 h-7 text-slate-400" /></div>
            <p className="text-slate-600 font-medium">Belum ada pesanan</p>
            <Link to="/produk" className="inline-block mt-4"><Button className="rounded-full bg-[#00133F] text-white">Mulai Belanja</Button></Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => {
              const st = statusOf(o.order_status);
              const canCancel = o.order_status === "menunggu_pembayaran";
              return (
                <div key={o.id} data-testid={`order-${o.order_number}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-heading font-semibold text-[#00133F]">#{o.order_number}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{o.payment_status === "paid" ? "Lunas" : "Belum bayar"}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1.5">{o.items.length} item · {new Date(o.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                      {o.tracking_number && <p className="text-xs text-[#00133F] mt-1" data-testid={`order-tracking-${o.order_number}`}>No. Resi: <span className="font-mono font-semibold">{o.tracking_number}</span></p>}
                      {o.resi_image_file_id && (
                        <button onClick={() => setResiOrder(o)} data-testid={`view-resi-${o.order_number}`}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F0005A] hover:underline">
                          <ImageIcon className="w-3.5 h-3.5" /> Lihat Bukti Resi
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-[#F0005A]">{formatRupiah(o.total)}</span>
                      <Link to={`/pembayaran/${o.id}`} className="p-1.5 rounded-full hover:bg-slate-100" data-testid={`order-open-${o.order_number}`}><ArrowRight className="w-4 h-4 text-slate-400" /></Link>
                    </div>
                  </div>
                  {canCancel && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => openCancel(o)} data-testid={`cancel-order-${o.order_number}`}
                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50">
                        <XCircle className="w-4 h-4 mr-1.5" /> Batalkan Pesanan
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!cancelOrder} onOpenChange={(v) => { if (!v) setCancelOrder(null); }}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Batalkan Pesanan {cancelOrder ? `#${cancelOrder.order_number}` : ""}</DialogTitle>
            <DialogDescription>Pilih alasan pembatalan. Pesanan hanya dapat dibatalkan selagi berstatus Menunggu Pembayaran.</DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-2">
            {REASONS.map((r) => (
              <label key={r.code} htmlFor={`reason-${r.code}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-[#F0005A]">
                <RadioGroupItem value={r.code} id={`reason-${r.code}`} data-testid={`reason-${r.code}`} />
                <span className="text-sm text-slate-700">{r.label}</span>
              </label>
            ))}
          </RadioGroup>
          {reason === "lainnya" && (
            <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} placeholder="Tuliskan alasan Anda..."
              className="rounded-xl mt-1" data-testid="reason-text" />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOrder(null)} className="rounded-full">Kembali</Button>
            <Button onClick={confirmCancel} disabled={cancelling} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ya, Batalkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResiDialog order={resiOrder} onClose={() => setResiOrder(null)} />
      <Footer />
    </div>
  );
}
